import posthog from 'posthog-js'

export interface TrackingService {
  trackEvent: (event: string, properties?: Record<string, unknown>) => void
  trackUser: (user?: { id: string; name?: string; email?: string }) => void
  trackLogout: () => void
}

export class PostHogService implements TrackingService {
  private isInitialized = false

  public constructor() {
    this.initialize()
  }

  private initialize(): void {
    const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY
    const posthogHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST

    if (!posthogKey) {
      console.error('PostHog key is missing')
      return
    }

    if (import.meta.env.DEV) {
      console.info('PostHog tracking disabled in development')
      return
    }

    try {
      posthog.init(posthogKey, {
        api_host: posthogHost || 'https://app.posthog.com',
        debug: false,
        persistence: 'localStorage',
      })
      this.isInitialized = true
    } catch (error) {
      console.error('PostHog initialization error:', error)
    }
  }

  public trackEvent = (
    event: string,
    properties?: Record<string, unknown>
  ): void => {
    if (import.meta.env.DEV) {
      console.info('PostHog Event (DEV):', event, properties)
      return
    }

    if (!this.isInitialized) {
      return
    }

    try {
      posthog.capture(event, {
        timestamp: new Date().toISOString(),
        ...properties,
      })
    } catch (error) {
      console.error('PostHog track error:', error)
    }
  }

  public trackUser = (user?: {
    id: string
    name?: string
    email?: string
  }): void => {
    if (import.meta.env.DEV) {
      console.info('PostHog User (DEV):', user)
      return
    }

    if (!this.isInitialized) {
      return
    }

    try {
      if (user?.id) {
        posthog.identify(user.id, {
          name: user.name,
          email: user.email,
        })
      }
    } catch (error) {
      console.error('PostHog user tracking error:', error)
    }
  }

  public trackLogout = (): void => {
    if (import.meta.env.DEV) {
      console.info('PostHog Logout (DEV)')
      return
    }

    this.trackEvent('User Logout')

    if (this.isInitialized) {
      try {
        posthog.reset()
      } catch (error) {
        console.error('PostHog reset error:', error)
      }
    }
  }
}
